import React, { useRef, useState } from "react";
import axios from "axios";
import formatApiError from "../lib/apiError";
import {
  Download,
  Trash2,
  Upload,
  Settings as SettingsIcon,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Sparkles,
  Inbox,
  CalendarClock,
  Mail,
  Send,
  KeyRound,
  Eye,
  EyeOff,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Super Admin → Settings page.
 *
 * Three actions:
 *   1. Export Data  — downloads a JSON file with every collection
 *   2. Clear Data   — wipes everything except the super admin account
 *   3. Import Data  — uploads a JSON file and restores its contents
 *
 * All endpoints are protected on the backend (role === "admin").
 */
export default function SettingsPage() {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(null); // "export" | "clear" | "import" | null
  const [banner, setBanner] = useState(null); // { kind: "ok"|"err", text: string }

  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const [pendingImportFile, setPendingImportFile] = useState(null);
  const [confirmImportOpen, setConfirmImportOpen] = useState(false);

  const [confirmSeedOpen, setConfirmSeedOpen] = useState(false);

  const [confirmClearTicketsOpen, setConfirmClearTicketsOpen] = useState(false);
  const [confirmGenTimetableOpen, setConfirmGenTimetableOpen] = useState(false);

  // ─── SMTP settings modal state ──────────────────────────────────────────
  const [smtpModalOpen, setSmtpModalOpen] = useState(false);
  const [smtpForm, setSmtpForm] = useState({
    host: "smtp.gmail.com", port: 587, user: "",
    password: "", from_addr: "", from_name: "MediMagic", password_set: false,
  });
  const [smtpLoading, setSmtpLoading]   = useState(false);
  const [smtpErr, setSmtpErr]           = useState("");
  const [smtpTestEmail, setSmtpTestEmail] = useState("");

  // ─── Change Password modal state ─────────────────────────────────────────
  const [chpwdModalOpen, setChpwdModalOpen] = useState(false);
  const [chpwdForm, setChpwdForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [chpwdLoading, setChpwdLoading] = useState(false);
  const [chpwdErr, setChpwdErr] = useState("");
  const [chpwdShow, setChpwdShow] = useState({ current: false, newpwd: false, confirm: false });

  const showOk = (text) => setBanner({ kind: "ok", text });
  const showErr = (text) => setBanner({ kind: "err", text });
  const clearBanner = () => setBanner(null);

  // ────────────────────────────── EXPORT ──────────────────────────────
  const handleExport = async () => {
    clearBanner();
    setBusy("export");
    try {
      const res = await axios.get(`${API}/api/admin/settings/export`, {
        withCredentials: true,
        responseType: "blob",
      });

      // Pull filename from Content-Disposition if present, else generate one
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename =
        (match && match[1]) ||
        `medimagic-export-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.json`;

      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      showOk(`Exported successfully as ${filename}`);
    } catch (e) {
      showErr(formatApiError(e, "Export failed"));
    } finally {
      setBusy(null);
    }
  };

  // ────────────────────────────── CLEAR ──────────────────────────────
  const openClearDialog = () => {
    clearBanner();
    setConfirmText("");
    setConfirmClearOpen(true);
  };

  const handleClear = async () => {
    setBusy("clear");
    try {
      const res = await axios.post(
        `${API}/api/admin/settings/clear`,
        {},
        { withCredentials: true }
      );
      const totalDeleted = Object.values(res.data?.deleted || {}).reduce(
        (a, b) => a + (b || 0),
        0
      );
      showOk(
        `All data cleared. ${totalDeleted} record${totalDeleted === 1 ? "" : "s"} deleted. Super Admin preserved.`
      );
      setConfirmClearOpen(false);
    } catch (e) {
      showErr(formatApiError(e, "Clear failed"));
    } finally {
      setBusy(null);
    }
  };

  // ────────────────────────────── CLEAR TICKETS ONLY ──────────────────────────────
  const openClearTicketsDialog = () => {
    clearBanner();
    setConfirmClearTicketsOpen(true);
  };

  const handleClearTickets = async () => {
    setBusy("clear-tickets");
    try {
      const res = await axios.post(
        `${API}/api/admin/settings/clear-tickets`,
        {},
        { withCredentials: true }
      );
      const d = res.data?.deleted || {};
      const t = d.tickets || 0;
      const n = d.notifications || 0;
      showOk(
        `${t} ticket${t === 1 ? "" : "s"} and ${n} related notification${n === 1 ? "" : "s"} deleted.`
      );
      setConfirmClearTicketsOpen(false);
    } catch (e) {
      showErr(formatApiError(e, "Clearing tickets failed"));
    } finally {
      setBusy(null);
    }
  };

  // ────────────────────────────── SMTP SETTINGS ──────────────────────────────
  const openSmtpModal = async () => {
    clearBanner();
    setSmtpErr("");
    setSmtpLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/settings/smtp`, { withCredentials: true });
      setSmtpForm({
        host: data.host || "smtp.gmail.com",
        port: data.port || 587,
        user: data.user || "",
        password: data.password || "",
        from_addr: data.from_addr || "",
        from_name: data.from_name || "MediMagic",
        password_set: !!data.password_set,
      });
      setSmtpModalOpen(true);
    } catch (e) {
      showErr(formatApiError(e, "Could not load SMTP settings"));
    } finally {
      setSmtpLoading(false);
    }
  };

  const saveSmtpSettings = async () => {
    setSmtpErr(""); setSmtpLoading(true);
    try {
      const payload = {
        host:      smtpForm.host.trim(),
        port:      Number(smtpForm.port) || 587,
        user:      smtpForm.user.trim(),
        // If the user hasn't typed a new password, send empty so the
        // backend keeps the existing one.
        password:  smtpForm.password.startsWith("•") ? "" : smtpForm.password,
        from_addr: smtpForm.from_addr.trim(),
        from_name: smtpForm.from_name.trim() || "MediMagic",
      };
      await axios.put(`${API}/api/admin/settings/smtp`, payload, { withCredentials: true });
      showOk("SMTP settings saved.");
      setSmtpModalOpen(false);
    } catch (e) {
      setSmtpErr(formatApiError(e, "Could not save SMTP settings"));
    } finally {
      setSmtpLoading(false);
    }
  };

  const sendSmtpTest = async () => {
    if (!smtpTestEmail.trim() || !smtpTestEmail.includes("@")) {
      setSmtpErr("Enter a valid test recipient email."); return;
    }
    setSmtpErr(""); setSmtpLoading(true);
    try {
      // Save first so the test uses the latest creds
      await saveSmtpSettings();
      await axios.post(
        `${API}/api/admin/settings/smtp/test`,
        { to: smtpTestEmail.trim() },
        { withCredentials: true }
      );
      showOk(`Test email sent to ${smtpTestEmail.trim()}.`);
      setSmtpTestEmail("");
    } catch (e) {
      setSmtpErr(formatApiError(e, "Test send failed"));
    } finally {
      setSmtpLoading(false);
    }
  };

  // ────────────────────────────── GENERATE TIMETABLE ──────────────────────────────
  const openGenTimetableDialog = () => {
    clearBanner();
    setConfirmGenTimetableOpen(true);
  };

  const handleGenerateTimetable = async () => {
    setBusy("gen-timetable");
    try {
      const res = await axios.post(
        `${API}/api/admin/settings/generate-timetable`,
        {},
        { withCredentials: true }
      );
      const d = res.data || {};
      const created = d.created || 0;
      const wiped = d.wiped_previous_generated || 0;
      const faculty = d.faculty_covered || 0;
      if (faculty === 0) {
        showErr("No faculty with a subject was found — add some first.");
      } else {
        showOk(
          `${created} timetable ${created === 1 ? "entry" : "entries"} created across ${faculty} faculty` +
          (wiped > 0 ? ` (replaced ${wiped} previously generated entries).` : ".")
        );
      }
      setConfirmGenTimetableOpen(false);
    } catch (e) {
      showErr(formatApiError(e, "Timetable generation failed"));
    } finally {
      setBusy(null);
    }
  };

  // ────────────────────────────── SEED DUMMY DATA ──────────────────────────────
  const openSeedDialog = () => {
    clearBanner();
    setConfirmSeedOpen(true);
  };

  const handleSeed = async () => {
    setBusy("seed");
    try {
      const res = await axios.post(
        `${API}/api/admin/settings/seed-dummy`,
        {},
        { withCredentials: true, timeout: 120000 }
      );
      const s = res.data?.seeded || {};
      const parts = [];
      if (s.colleges)          parts.push(`${s.colleges} colleges`);
      if (s.professors)        parts.push(`${s.professors} professors`);
      if (s.subjects)          parts.push(`${s.subjects} subjects`);
      if (s.timetable_entries) parts.push(`${s.timetable_entries} timetable entries`);
      if (s.tickets)           parts.push(`${s.tickets} tickets`);
      showOk(
        `Dummy data created${parts.length ? `: ${parts.join(", ")}.` : "."}` +
        (s.demo_password ? ` Demo user password: ${s.demo_password}.` : "")
      );
      setConfirmSeedOpen(false);
    } catch (e) {
      showErr(formatApiError(e, "Seeding failed"));
    } finally {
      setBusy(null);
    }
  };

  // ────────────────────────────── IMPORT ──────────────────────────────
  const triggerFilePicker = () => {
    clearBanner();
    fileInputRef.current?.click();
  };

  const onFileChosen = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = ""; // allow re-selecting same file later
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".json")) {
      showErr("Please choose a .json file");
      return;
    }
    setPendingImportFile(f);
    setConfirmImportOpen(true);
  };

  const handleImport = async () => {
    if (!pendingImportFile) return;
    setBusy("import");
    try {
      const form = new FormData();
      form.append("file", pendingImportFile);
      const res = await axios.post(
        `${API}/api/admin/settings/import`,
        form,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      const inserted = res.data?.inserted || {};
      const total = Object.values(inserted).reduce((a, b) => a + (b || 0), 0);
      showOk(
        `Imported ${total} record${total === 1 ? "" : "s"} across ${Object.keys(inserted).length} collection${
          Object.keys(inserted).length === 1 ? "" : "s"
        }.`
      );
      setConfirmImportOpen(false);
      setPendingImportFile(null);
    } catch (e) {
      showErr(formatApiError(e, "Import failed"));
    } finally {
      setBusy(null);
    }
  };

  // ────────────────────────────── CHANGE PASSWORD ──────────────────────────────
  const openChpwdModal = () => {
    clearBanner();
    setChpwdErr("");
    setChpwdForm({ current_password: "", new_password: "", confirm_password: "" });
    setChpwdShow({ current: false, newpwd: false, confirm: false });
    setChpwdModalOpen(true);
  };

  const handleChangePassword = async () => {
    setChpwdErr("");
    if (!chpwdForm.current_password) { setChpwdErr("Current password is required."); return; }
    if (!chpwdForm.new_password) { setChpwdErr("New password is required."); return; }
    if (chpwdForm.new_password.length < 6) { setChpwdErr("New password must be at least 6 characters."); return; }
    if (chpwdForm.new_password !== chpwdForm.confirm_password) { setChpwdErr("New password and confirm password do not match."); return; }
    setChpwdLoading(true);
    try {
      await axios.post(
        `${API}/api/auth/change-password`,
        {
          current_password: chpwdForm.current_password,
          new_password: chpwdForm.new_password,
          confirm_password: chpwdForm.confirm_password,
        },
        { withCredentials: true }
      );
      showOk("Password changed successfully.");
      setChpwdModalOpen(false);
    } catch (e) {
      setChpwdErr(formatApiError(e, "Could not change password."));
    } finally {
      setChpwdLoading(false);
    }
  };

  // ────────────────────────────── RENDER ──────────────────────────────
  return (
    <div className="page-wrap" data-testid="settings-page">
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-sub">
          Manage application data — export a backup, wipe everything, or import a previous bundle.
        </p>
      </div>

      {banner && (
        <div
          className={`settings-banner ${banner.kind === "ok" ? "ok" : "err"}`}
          data-testid="settings-banner"
        >
          {banner.kind === "ok" ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
          <span>{banner.text}</span>
          <button className="settings-banner-close" onClick={clearBanner} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      <div className="settings-grid">
        {/* Export */}
        <div className="settings-card">
          <div className="settings-card-icon settings-card-icon--blue">
            <Download size={22} />
          </div>
          <h3 className="settings-card-title">Export Data</h3>
          <p className="settings-card-desc">
            Download a full JSON snapshot of every collection in the database. Useful for backups
            before destructive operations.
          </p>
          <button
            className="btn-primary settings-card-action"
            onClick={handleExport}
            disabled={busy !== null}
            data-testid="settings-export-btn"
          >
            {busy === "export" ? <span className="btn-spinner" /> : <Download size={16} />}
            {busy === "export" ? "Exporting…" : "Export Data"}
          </button>
        </div>

        {/* Clear */}
        <div className="settings-card">
          <div className="settings-card-icon settings-card-icon--red">
            <Trash2 size={22} />
          </div>
          <h3 className="settings-card-title">Clear Data</h3>
          <p className="settings-card-desc">
            Permanently delete every record from every collection. The Super Admin account is
            preserved so you can still log in.
          </p>
          <button
            className="btn-danger settings-card-action"
            onClick={openClearDialog}
            disabled={busy !== null}
            data-testid="settings-clear-btn"
          >
            {busy === "clear" ? <span className="btn-spinner" /> : <Trash2 size={16} />}
            {busy === "clear" ? "Clearing…" : "Clear Data"}
          </button>
        </div>

        {/* Clear Tickets only */}
        <div className="settings-card">
          <div className="settings-card-icon settings-card-icon--red">
            <Inbox size={22} />
          </div>
          <h3 className="settings-card-title">Clear All Tickets</h3>
          <p className="settings-card-desc">
            Delete every ticket and its related notifications. The ticket counter resets so the next ticket starts from MM-0001.
            All other data (users, colleges, master data, timetable) is left untouched.
          </p>
          <button
            className="btn-danger settings-card-action"
            onClick={openClearTicketsDialog}
            disabled={busy !== null}
            data-testid="settings-clear-tickets-btn"
          >
            {busy === "clear-tickets" ? <span className="btn-spinner" /> : <Inbox size={16} />}
            {busy === "clear-tickets" ? "Clearing…" : "Clear All Tickets"}
          </button>
        </div>

        {/* SMTP Settings */}
        <div className="settings-card">
          <div className="settings-card-icon settings-card-icon--purple">
            <Mail size={22} />
          </div>
          <h3 className="settings-card-title">Email (SMTP) Settings</h3>
          <p className="settings-card-desc">
            Configure the mailbox that sends login OTPs and system notifications. Saved here
            instead of <code>.env</code>, so you can update it anytime.
          </p>
          <button
            className="btn-primary settings-card-action"
            onClick={openSmtpModal}
            disabled={busy !== null || smtpLoading}
            data-testid="settings-smtp-btn"
          >
            <Mail size={16} /> Configure SMTP
          </button>
        </div>

        {/* Change Password */}
        <div className="settings-card">
          <div className="settings-card-icon settings-card-icon--blue">
            <KeyRound size={22} />
          </div>
          <h3 className="settings-card-title">Change Password</h3>
          <p className="settings-card-desc">
            Update the Super Admin login password. You will need to enter your current password to confirm.
          </p>
          <button
            className="btn-primary settings-card-action"
            onClick={openChpwdModal}
            disabled={busy !== null}
            data-testid="settings-chpwd-btn"
          >
            <KeyRound size={16} /> Change Password
          </button>
        </div>

        {/* Generate Timetable */}
        <div className="settings-card">
          <div className="settings-card-icon settings-card-icon--green">
            <CalendarClock size={22} />
          </div>
          <h3 className="settings-card-title">Generate Timetable</h3>
          <p className="settings-card-desc">
            Auto-populate the timetable with realistic IT-Admin-style entries — one weekday class per faculty
            across the last 90 days, with a 70 / 30 split between completed and cancelled sessions.
            Replaces any previously generated entries; manually-added rows are left alone.
          </p>
          <button
            className="btn-primary settings-card-action"
            onClick={openGenTimetableDialog}
            disabled={busy !== null}
            data-testid="settings-gen-timetable-btn"
          >
            {busy === "gen-timetable" ? <span className="btn-spinner" /> : <CalendarClock size={16} />}
            {busy === "gen-timetable" ? "Generating…" : "Generate Timetable"}
          </button>
        </div>

        {/* Import */}
        <div className="settings-card">
          <div className="settings-card-icon settings-card-icon--green">
            <Upload size={22} />
          </div>
          <h3 className="settings-card-title">Import Data</h3>
          <p className="settings-card-desc">
            Restore from a previously exported JSON file. Existing data will be cleared first; the
            Super Admin account is always preserved.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            onChange={onFileChosen}
            style={{ display: "none" }}
            data-testid="settings-import-file"
          />
          <button
            className="btn-primary settings-card-action settings-card-action--green"
            onClick={triggerFilePicker}
            disabled={busy !== null}
            data-testid="settings-import-btn"
          >
            {busy === "import" ? <span className="btn-spinner" /> : <Upload size={16} />}
            {busy === "import" ? "Importing…" : "Import Data"}
          </button>
        </div>

        {/* Seed Dummy Data */}
        <div className="settings-card">
          <div className="settings-card-icon settings-card-icon--purple">
            <Sparkles size={22} />
          </div>
          <h3 className="settings-card-title">Create Dummy Data</h3>
          <p className="settings-card-desc">
            Populate the portal with a realistic demo dataset — colleges, subjects, professors,
            calendar entries and sample tickets. Useful for walk-throughs.
            <strong> Remove this option before go-live.</strong>
          </p>
          <button
            className="btn-primary settings-card-action settings-card-action--purple"
            onClick={openSeedDialog}
            disabled={busy !== null}
            data-testid="settings-seed-btn"
          >
            {busy === "seed" ? <span className="btn-spinner" /> : <Sparkles size={16} />}
            {busy === "seed" ? "Seeding…" : "Create Dummy Data"}
          </button>
        </div>
      </div>

      <div className="settings-footnote">
        <SettingsIcon size={14} />
        <span>All actions on this page are restricted to the Super Admin role.</span>
      </div>

      {/* ─── CLEAR confirmation dialog ─── */}
      {confirmClearOpen && (
        <div
          className="modal-overlay"
          onClick={() => busy !== "clear" && setConfirmClearOpen(false)}
        >
          <div className="modal-box modal-box-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3 className="modal-title" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <AlertTriangle size={18} color="#EF4444" />
                Clear ALL data?
              </h3>
            </div>
            <div className="modal-body">
              <p style={{ margin: 0, color: "var(--darker-grey)", fontSize: "0.92rem", lineHeight: 1.5 }}>
                This will permanently delete every country, state, college, user, ticket, lesson,
                timetable entry and more. The Super Admin account stays intact.
              </p>
              <p style={{ margin: 0, color: "var(--darker-grey)", fontSize: "0.92rem" }}>
                Type <strong>CLEAR</strong> below to confirm.
              </p>
              <input
                type="text"
                className="field-input"
                placeholder="CLEAR"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                autoFocus
                data-testid="settings-clear-confirm-input"
              />
            </div>
            <div className="modal-foot">
              <button
                className="btn-ghost"
                onClick={() => setConfirmClearOpen(false)}
                disabled={busy === "clear"}
              >
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={handleClear}
                disabled={confirmText !== "CLEAR" || busy === "clear"}
                data-testid="settings-clear-confirm-btn"
              >
                {busy === "clear" ? <span className="btn-spinner" /> : <Trash2 size={16} />}
                {busy === "clear" ? "Clearing…" : "Yes, clear everything"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── IMPORT confirmation dialog ─── */}
      {confirmImportOpen && pendingImportFile && (
        <div
          className="modal-overlay"
          onClick={() => busy !== "import" && setConfirmImportOpen(false)}
        >
          <div className="modal-box modal-box-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3 className="modal-title" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <AlertTriangle size={18} color="#EF4444" />
                Import data?
              </h3>
            </div>
            <div className="modal-body">
              <p style={{ margin: 0, color: "var(--darker-grey)", fontSize: "0.92rem", lineHeight: 1.5 }}>
                You are about to import <strong>{pendingImportFile.name}</strong> (
                {(pendingImportFile.size / 1024).toFixed(1)} KB).
              </p>
              <p style={{ margin: 0, color: "var(--darker-grey)", fontSize: "0.92rem", lineHeight: 1.5 }}>
                This will <strong>clear all existing data</strong> (except the Super Admin) before
                inserting the records from the file. This action cannot be undone.
              </p>
            </div>
            <div className="modal-foot">
              <button
                className="btn-ghost"
                onClick={() => {
                  setConfirmImportOpen(false);
                  setPendingImportFile(null);
                }}
                disabled={busy === "import"}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleImport}
                disabled={busy === "import"}
                data-testid="settings-import-confirm-btn"
              >
                {busy === "import" ? <span className="btn-spinner" /> : <Upload size={16} />}
                {busy === "import" ? "Importing…" : "Yes, import"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ─── SMTP settings modal ─── */}
      {smtpModalOpen && (
        <div className="modal-overlay" onClick={() => !smtpLoading && setSmtpModalOpen(false)}>
          <div className="modal-box modal-box-md" onClick={(e) => e.stopPropagation()} data-testid="smtp-modal">
            <div className="modal-head">
              <h3 className="modal-title" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Mail size={18} color="#7C3AED" /> Email (SMTP) Settings
              </h3>
              <button className="modal-close" onClick={() => setSmtpModalOpen(false)} disabled={smtpLoading}>
                <XCircle size={18} />
              </button>
            </div>

            {smtpErr && <div className="form-error">{smtpErr}</div>}

            <div className="modal-body">
              <div className="field-row">
                <div className="field" style={{ flex: 2 }}>
                  <label className="field-label">Host</label>
                  <input
                    className="field-input"
                    value={smtpForm.host}
                    onChange={(e) => setSmtpForm((f) => ({ ...f, host: e.target.value }))}
                    placeholder="smtp.gmail.com"
                    data-testid="smtp-host-input"
                  />
                </div>
                <div className="field">
                  <label className="field-label">Port</label>
                  <input
                    type="number"
                    className="field-input"
                    value={smtpForm.port}
                    onChange={(e) => setSmtpForm((f) => ({ ...f, port: e.target.value }))}
                    placeholder="587"
                    data-testid="smtp-port-input"
                  />
                </div>
              </div>

              <div className="field">
                <label className="field-label">Sender Email (SMTP user) <span className="required">*</span></label>
                <input
                  type="email"
                  className="field-input"
                  value={smtpForm.user}
                  onChange={(e) => setSmtpForm((f) => ({ ...f, user: e.target.value }))}
                  placeholder="alerts@yourdomain.com"
                  data-testid="smtp-user-input"
                />
              </div>

              <div className="field">
                <label className="field-label">
                  App Password <span className="required">*</span>
                  {smtpForm.password_set && (
                    <span style={{ marginLeft: 8, color: "#10B981", fontSize: 11 }}>
                      Already saved — leave blank to keep it.
                    </span>
                  )}
                </label>
                <input
                  type="password"
                  className="field-input"
                  value={smtpForm.password.startsWith("•") ? "" : smtpForm.password}
                  onChange={(e) => setSmtpForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder={smtpForm.password_set ? "•••• •••• •••• ••••" : "abcdefghijklmnop"}
                  autoComplete="new-password"
                  data-testid="smtp-password-input"
                />
                <p style={{ margin: "4px 0 0", color: "#94A3B8", fontSize: 11.5 }}>
                  For Gmail: paste the 16-char App Password (no spaces).
                </p>
              </div>

              <div className="field-row">
                <div className="field">
                  <label className="field-label">From Address (optional)</label>
                  <input
                    type="email"
                    className="field-input"
                    value={smtpForm.from_addr}
                    onChange={(e) => setSmtpForm((f) => ({ ...f, from_addr: e.target.value }))}
                    placeholder="defaults to sender email"
                    data-testid="smtp-fromaddr-input"
                  />
                </div>
                <div className="field">
                  <label className="field-label">From Name</label>
                  <input
                    className="field-input"
                    value={smtpForm.from_name}
                    onChange={(e) => setSmtpForm((f) => ({ ...f, from_name: e.target.value }))}
                    placeholder="MediMagic"
                    data-testid="smtp-fromname-input"
                  />
                </div>
              </div>

              <div className="field" style={{ marginTop: 18, paddingTop: 18, borderTop: "1px solid #E2E8F0" }}>
                <label className="field-label">Send a test email</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="email"
                    className="field-input"
                    style={{ flex: 1 }}
                    value={smtpTestEmail}
                    onChange={(e) => setSmtpTestEmail(e.target.value)}
                    placeholder="your-test@inbox.com"
                    data-testid="smtp-test-input"
                  />
                  <button
                    className="btn-ghost"
                    onClick={sendSmtpTest}
                    disabled={smtpLoading || !smtpTestEmail}
                    data-testid="smtp-test-btn"
                  >
                    <Send size={14} /> Send Test
                  </button>
                </div>
              </div>
            </div>

            <div className="modal-foot">
              <button className="btn-ghost" onClick={() => setSmtpModalOpen(false)} disabled={smtpLoading}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={saveSmtpSettings}
                disabled={smtpLoading}
                data-testid="smtp-save-btn"
              >
                {smtpLoading ? <span className="btn-spinner" /> : <CheckCircle2 size={16} />}
                {smtpLoading ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── GENERATE TIMETABLE confirmation dialog ─── */}
      {confirmGenTimetableOpen && (
        <div
          className="modal-overlay"
          onClick={() => busy !== "gen-timetable" && setConfirmGenTimetableOpen(false)}
        >
          <div className="modal-box modal-box-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3 className="modal-title" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <CalendarClock size={18} color="#3072FF" />
                Generate timetable?
              </h3>
            </div>
            <div className="modal-body">
              <p style={{ margin: 0, color: "var(--darker-grey)", fontSize: "0.92rem", lineHeight: 1.5 }}>
                For every faculty (with a subject set), one weekday class will be created across the last 90 days
                — random time slot, random duration, and a random NMC lesson from their subject. About 70 % are
                marked as completed; the rest are flagged with a cancellation reason.
              </p>
              <p style={{ margin: 0, color: "var(--darker-grey)", fontSize: "0.92rem", lineHeight: 1.5 }}>
                Any timetable rows from a previous run of this same button are wiped first. Real entries added
                by IT Admins are <strong>not</strong> touched.
              </p>
            </div>
            <div className="modal-foot">
              <button
                className="btn-ghost"
                onClick={() => setConfirmGenTimetableOpen(false)}
                disabled={busy === "gen-timetable"}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleGenerateTimetable}
                disabled={busy === "gen-timetable"}
                data-testid="settings-gen-timetable-confirm-btn"
              >
                {busy === "gen-timetable" ? <span className="btn-spinner" /> : <CalendarClock size={16} />}
                {busy === "gen-timetable" ? "Generating…" : "Yes, generate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── CLEAR TICKETS confirmation dialog ─── */}
      {confirmClearTicketsOpen && (
        <div
          className="modal-overlay"
          onClick={() => busy !== "clear-tickets" && setConfirmClearTicketsOpen(false)}
        >
          <div className="modal-box modal-box-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3 className="modal-title" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <AlertTriangle size={18} color="#EF4444" />
                Clear all tickets?
              </h3>
            </div>
            <div className="modal-body">
              <p style={{ margin: 0, color: "var(--darker-grey)", fontSize: "0.92rem", lineHeight: 1.5 }}>
                This will permanently delete every ticket, its conversation thread, attachments, and the
                related notifications. The ticket counter is reset so the next ticket starts at <strong>MM-0001</strong>.
              </p>
              <p style={{ margin: 0, color: "var(--darker-grey)", fontSize: "0.92rem", lineHeight: 1.5 }}>
                Users, colleges, master data and timetable entries are <strong>not</strong> affected.
              </p>
            </div>
            <div className="modal-foot">
              <button
                className="btn-ghost"
                onClick={() => setConfirmClearTicketsOpen(false)}
                disabled={busy === "clear-tickets"}
              >
                Cancel
              </button>
              <button
                className="btn-danger"
                onClick={handleClearTickets}
                disabled={busy === "clear-tickets"}
                data-testid="settings-clear-tickets-confirm-btn"
              >
                {busy === "clear-tickets" ? <span className="btn-spinner" /> : <Inbox size={16} />}
                {busy === "clear-tickets" ? "Clearing…" : "Yes, clear all tickets"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── SEED confirmation dialog ─── */}
      {confirmSeedOpen && (
        <div
          className="modal-overlay"
          onClick={() => busy !== "seed" && setConfirmSeedOpen(false)}
        >
          <div className="modal-box modal-box-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3 className="modal-title" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Sparkles size={18} color="#7C3AED" />
                Create dummy data?
              </h3>
            </div>
            <div className="modal-body">
              <p style={{ margin: 0, color: "var(--darker-grey)", fontSize: "0.92rem", lineHeight: 1.5 }}>
                This will insert a fresh demo dataset: <strong>countries, states, cities,
                universities, colleges, subjects, lessons, professors, ~30–60 calendar entries per
                college, sample tickets and contacts</strong>.
              </p>
              <p style={{ margin: 0, color: "var(--darker-grey)", fontSize: "0.92rem", lineHeight: 1.5 }}>
                Any previous demo records are replaced. Your real data — anything you&apos;ve added
                yourself — is left untouched. Demo users sign in with password{" "}
                <strong>Welcome@123</strong>.
              </p>
              <p style={{ margin: 0, color: "var(--darker-grey)", fontSize: "0.85rem" }}>
                Seeding can take 20–60 seconds.
              </p>
            </div>
            <div className="modal-foot">
              <button
                className="btn-ghost"
                onClick={() => setConfirmSeedOpen(false)}
                disabled={busy === "seed"}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                style={{ background: "#7C3AED" }}
                onClick={handleSeed}
                disabled={busy === "seed"}
                data-testid="settings-seed-confirm-btn"
              >
                {busy === "seed" ? <span className="btn-spinner" /> : <Sparkles size={16} />}
                {busy === "seed" ? "Seeding…" : "Yes, create dummy data"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── CHANGE PASSWORD modal ─── */}
      {chpwdModalOpen && (
        <div className="modal-overlay" onClick={() => !chpwdLoading && setChpwdModalOpen(false)}>
          <div className="modal-box modal-box-md" onClick={(e) => e.stopPropagation()} data-testid="chpwd-modal">
            <div className="modal-head">
              <h3 className="modal-title" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <KeyRound size={18} color="#3072FF" /> Change Password
              </h3>
              <button className="modal-close" onClick={() => setChpwdModalOpen(false)} disabled={chpwdLoading}>
                <XCircle size={18} />
              </button>
            </div>

            {chpwdErr && <div className="form-error">{chpwdErr}</div>}

            <div className="modal-body">
              {/* Current Password */}
              <div className="field">
                <label className="field-label">Current Password <span className="required">*</span></label>
                <div style={{ position: "relative" }}>
                  <input
                    type={chpwdShow.current ? "text" : "password"}
                    className="field-input"
                    value={chpwdForm.current_password}
                    onChange={(e) => setChpwdForm((f) => ({ ...f, current_password: e.target.value }))}
                    placeholder="Enter your current password"
                    autoComplete="current-password"
                    data-testid="chpwd-current-input"
                    style={{ paddingRight: 40 }}
                  />
                  <button
                    type="button"
                    onClick={() => setChpwdShow((s) => ({ ...s, current: !s.current }))}
                    style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94A3B8", padding: 0 }}
                    tabIndex={-1}
                  >
                    {chpwdShow.current ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div className="field">
                <label className="field-label">New Password <span className="required">*</span></label>
                <div style={{ position: "relative" }}>
                  <input
                    type={chpwdShow.newpwd ? "text" : "password"}
                    className="field-input"
                    value={chpwdForm.new_password}
                    onChange={(e) => setChpwdForm((f) => ({ ...f, new_password: e.target.value }))}
                    placeholder="At least 6 characters"
                    autoComplete="new-password"
                    data-testid="chpwd-new-input"
                    style={{ paddingRight: 40 }}
                  />
                  <button
                    type="button"
                    onClick={() => setChpwdShow((s) => ({ ...s, newpwd: !s.newpwd }))}
                    style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94A3B8", padding: 0 }}
                    tabIndex={-1}
                  >
                    {chpwdShow.newpwd ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="field">
                <label className="field-label">Confirm New Password <span className="required">*</span></label>
                <div style={{ position: "relative" }}>
                  <input
                    type={chpwdShow.confirm ? "text" : "password"}
                    className="field-input"
                    value={chpwdForm.confirm_password}
                    onChange={(e) => setChpwdForm((f) => ({ ...f, confirm_password: e.target.value }))}
                    placeholder="Re-enter new password"
                    autoComplete="new-password"
                    data-testid="chpwd-confirm-input"
                    style={{ paddingRight: 40 }}
                  />
                  <button
                    type="button"
                    onClick={() => setChpwdShow((s) => ({ ...s, confirm: !s.confirm }))}
                    style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94A3B8", padding: 0 }}
                    tabIndex={-1}
                  >
                    {chpwdShow.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </div>

            <div className="modal-foot">
              <button className="btn-ghost" onClick={() => setChpwdModalOpen(false)} disabled={chpwdLoading}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleChangePassword}
                disabled={chpwdLoading}
                data-testid="chpwd-save-btn"
              >
                {chpwdLoading ? <span className="btn-spinner" /> : <KeyRound size={16} />}
                {chpwdLoading ? "Updating…" : "Update Password"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
